<script setup lang="ts">
import {computed, ref, watch} from "vue";
import PhoneFrame from "@/birthday-gift/components/phone-preview/PhoneFrame.vue";
import GiftRcsConversation from "@/birthday-gift/components/phone-preview/GiftRcsConversation.vue";
import GiftQrPagePreview from "@/birthday-gift/components/phone-preview/GiftQrPagePreview.vue";
import GiftVideoPreview, {GiftVideoSource} from "@/birthday-gift/components/phone-preview/GiftVideoPreview.vue";
import {GiftValidityDays, GiftVideo} from "@/birthday-gift/interfaces";
import useBirthdayStockVideos from "@/birthday-gift/composables/useBirthdayStockVideos";

const props = defineProps<{
    pharmacyName: string;
    validityDays: GiftValidityDays;
    video: GiftVideo;
}>();

const screen = ref<'message' | 'qr'>('message');

/** Whether the video button was clicked: the video plays over the message screen. */
const showVideo = ref(false);

const {data: videos} = useBirthdayStockVideos();

const videoLabel = computed<string | null>(() => {
    if (props.video.video_type === 'youtube') {
        return props.video.video_url ? 'Voir votre vidéo' : null;
    }

    const title = videos.value?.find((video) => video.id === props.video.video_id)?.title;

    return title ? `Voir : ${title}` : null;
});

/**
 * YouTube watch pages refuse framing: only the /embed/ form is playable
 * inside the preview. Null when no video id can be extracted from the URL.
 */
const youtubeEmbedUrl = (url: string): string | null => {
    try {
        const parsed = new URL(url);
        const id = /(^|\.)youtu\.be$/i.test(parsed.hostname)
            ? parsed.pathname.split('/')[1]
            : parsed.searchParams.get('v')
                ?? parsed.pathname.match(/\/(?:shorts|embed|live)\/([\w-]+)/)?.[1];

        return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : null;
    } catch {
        return null;
    }
};

const videoSource = computed<GiftVideoSource | null>(() => {
    if (props.video.video_type === 'youtube') {
        const url = props.video.video_url ? youtubeEmbedUrl(props.video.video_url) : null;

        return url ? {type: 'youtube', url} : null;
    }

    const url = videos.value?.find((video) => video.id === props.video.video_id)?.url;

    return url ? {type: 'file', url} : null;
});

// Selecting another video (or clearing it) closes the player.
watch(() => props.video, () => {
    showVideo.value = false;
});
</script>

<template>
  <div class="flex flex-col items-center gap-3">
    <span class="text-xs text-muted-foreground">Ce que voit le patient</span>

    <div class="flex gap-1 rounded-full bg-gray-light p-1">
      <button
        v-for="tab in [{id: 'message', label: 'Message'}, {id: 'qr', label: 'Page QR'}]"
        :key="tab.id"
        type="button"
        class="rounded-full px-3 py-1 text-xs font-medium transition-colors"
        :class="screen === tab.id ? 'bg-white text-primary shadow-sm' : 'text-muted-foreground'"
        @click="screen = tab.id as 'message' | 'qr'; showVideo = false"
      >
        {{ tab.label }}
      </button>
    </div>

    <PhoneFrame>
      <GiftVideoPreview
        v-if="screen === 'message' && showVideo"
        :source="videoSource"
        @back="showVideo = false"
      />
      <GiftRcsConversation
        v-else-if="screen === 'message'"
        :pharmacy-name="pharmacyName"
        first-name="Marie"
        :video-label="videoLabel"
        @show-qr="screen = 'qr'"
        @show-video="showVideo = true"
      />
      <GiftQrPagePreview
        v-else
        :pharmacy-name="pharmacyName"
        :validity-days="validityDays"
      />
    </PhoneFrame>

    <p class="max-w-[300px] text-center text-xs leading-relaxed text-muted-foreground">
      Le patient ne voit jamais le contenu du cadeau — seulement son QR code.
    </p>
  </div>
</template>
